import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FiSearch, FiChevronLeft, FiChevronRight, FiPlus, FiMoreVertical, FiEdit, FiEye, FiDownload, FiTrash2 } from 'react-icons/fi'
import apiService from '@/services/api'
import { useDebounce } from '@/hooks/useDebounce'
import AddPeanutLotModal from '@/pages/storage/AddPeanutLotModal'
import PeanutLotDetailsModal from '@/pages/storage/PeanutLotDetailsModal'
import LotTraceabilityModal from './LotTraceabilityModal'
import { formatDateToDDMMMYYYY } from '@/utils/dateFormat'
import PageSizeSelect from '@/components/common/PageSizeSelect'

/** ZACPAK Stock Recon - all columns from FCLStockRecon_EXP_Combined.xlsx */
interface StockReconRow {
  id: string
  buyContractNumber: string | null
  saleContractNumber: string | null
  vesselVoyage: string | null
  booking: string | null
  billOfLading: string | null
  eta: string | null
  ata: string | null
  containerNumber: string | null
  batchNumber: string | null
  containerDischargeDate: string | null
  containerGateIn: string | null
  containerGateOut: string | null
  unpackDateTime: string | null
  daysInWarehouse: number | null
  location: string | null
  marksAndNumbers: string | null
  product: string | null
  barcode: string | null
  pieces: number | null
  itemNumber: string | null
  weight: number | null
  dispatchNumber: string | null
  pickingListNumber: string | null
  bags: number | null
  fclLcl: string | null
  label: string | null
  technicalSheet: string | null
  dispatchDate: string | null
  status: string | null
  updatedAt: string
}

const formatDateTimeToDDMMYYYYHHMM = (date: string | null): string => {
  if (!date) return ''
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return `${day}/${month}/${year} ${hours}:${minutes}`
}

const StorageInventory = () => {
  const [searchParams] = useSearchParams()
  const [currentPage, setCurrentPage] = useState(1)
  const [locationFilter, setLocationFilter] = useState('')
  const [stockView, setStockView] = useState<'current' | 'ytd'>('current')
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '')
  const debouncedSearch = useDebounce(searchQuery, 400)
  const debouncedLocation = useDebounce(locationFilter, 400)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const [isAddPeanutLotModalOpen, setIsAddPeanutLotModalOpen] = useState(false)
  const [isPeanutLotDetailsModalOpen, setIsPeanutLotDetailsModalOpen] = useState(false)
  const [isLotTraceabilityModalOpen, setIsLotTraceabilityModalOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<StockReconRow | null>(null)

  const toggleDropdown = (id: string) => {
    setOpenDropdownId(openDropdownId === id ? null : id)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId && !(event.target as Element).closest('[data-dropdown]')) {
        setOpenDropdownId(null)
      }
    }

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openDropdownId])

  const [rows, setRows] = useState<StockReconRow[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [importError, setImportError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  void importing
  const [resultsPerPage, setResultsPerPage] = useState(10)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingUpload, setPendingUpload] = useState<{ rowId: string; field: 'label' | 'technicalSheet' } | null>(null)
  const [uploadingRowField, setUploadingRowField] = useState<string | null>(null)

  // Inline edit state for Status column
  const [statusEdits, setStatusEdits] = useState<Record<string, string>>({})

  const handleStatusChange = (id: string, value: string) => {
    setStatusEdits((prev) => ({ ...prev, [id]: value }))
  }

  const saveStatus = useCallback(async (id: string) => {
    const value = statusEdits[id]
    if (value === undefined) return
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
      const token = localStorage.getItem('token')
      await fetch(`${baseUrl}/inventory/stock-recon/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ status: value }),
      })
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: value || null } : r)))
    } catch {
      // keep existing value on failure
    }
    setStatusEdits((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [statusEdits])

  const triggerFileUpload = (rowId: string, field: 'label' | 'technicalSheet') => {
    setPendingUpload({ rowId, field })
    fileInputRef.current?.click()
  }

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !pendingUpload) {
      e.target.value = ''
      setPendingUpload(null)
      return
    }

    const { rowId, field } = pendingUpload
    const uploadKey = `${rowId}-${field}`
    setUploadingRowField(uploadKey)

    try {
      const formData = new FormData()
      formData.append('file', file)
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
      const endpoint = field === 'label' ? 'label' : 'technical-sheet'
      const token = localStorage.getItem('token')
      const res = await fetch(`${baseUrl}/inventory/stock-recon/${rowId}/${endpoint}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      const data = await res.json()
      if (res.ok && data?.success) {
        const updatedUrl = field === 'label' ? data.data?.label : data.data?.technicalSheet
        setRows((prev) =>
          prev.map((r) =>
            r.id === rowId ? { ...r, [field]: updatedUrl || 'uploaded' } : r
          )
        )
      } else {
        setImportError(data?.error?.message || 'Upload failed')
      }
    } catch {
      setImportError('Upload failed. Please try again.')
    } finally {
      setUploadingRowField(null)
      setPendingUpload(null)
      e.target.value = ''
    }
  }

  const viewStockReconFile = async (rowId: string, field: 'label' | 'technicalSheet') => {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
      const endpoint = field === 'label' ? 'label' : 'technical-sheet'
      const token = localStorage.getItem('token')
      const headers: Record<string, string> = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await fetch(`${baseUrl}/inventory/stock-recon/${rowId}/${endpoint}/url`, { headers })
      const data = await res.json()
      const url = data?.data?.url
      if (url) {
        if (url.startsWith('/uploads/')) {
          const serverBase = baseUrl.replace(/\/api\/?$/, '')
          window.open(`${serverBase}${url}`, '_blank', 'noopener')
        } else {
          window.open(url, '_blank', 'noopener')
        }
      } else {
        setImportError('Could not retrieve file URL')
      }
    } catch {
      setImportError('Failed to open file')
    }
  }

  const fetchStockRecon = async () => {
    try {
      setLoading(true)
      setImportError(null)
      const params = new URLSearchParams()
      params.set('page', currentPage.toString())
      params.set('limit', resultsPerPage.toString())
      if (searchQuery) params.set('search', searchQuery)
      if (locationFilter) params.set('location', locationFilter)
      params.set('stockView', stockView)
      const res = await apiService.get<{ success: boolean; data: { rows: any[]; meta?: { total: number } } }>(
        `/inventory/stock-recon?${params.toString()}`
      )
      const data = (res as any)?.data ?? res
      const raw = data?.rows ?? []
      const meta = data?.meta ?? {}
      setRows(
        raw.map((r: any) => {
          const s = r.shipment
          return {
            id: r.id,
            buyContractNumber: r.buyContract?.contractNumber ?? s?.buyContract ?? null,
            saleContractNumber: r.saleContractNumber ?? s?.saleContract ?? null,
            vesselVoyage: r.vesselVoyage ?? s?.vessel ?? null,
            booking: r.bookingNumber ?? s?.booking ?? null,
            billOfLading: r.masterShippingNumber ?? s?.bl ?? null,
            eta: fmt(r.eta) || fmt(s?.eta),
            ata: fmt(r.ata),
            containerNumber: r.containerNumber ?? s?.containerNumber ?? null,
            batchNumber: r.batchNumber ?? null,
            containerDischargeDate: fmt(r.containerDischargeDate),
            containerGateIn: fmt(r.containerGateIn),
            containerGateOut: fmt(r.containerGateOut),
            unpackDateTime: fmt(r.unpackDateTime),
            daysInWarehouse: r.daysInWarehouse ?? null,
            location: r.location ?? null,
            marksAndNumbers: r.marksAndNumbers ?? null,
            product: r.product ?? s?.product ?? null,
            barcode: r.barcode ?? null,
            pieces: r.pieces ?? null,
            itemNumber: r.itemNumber ?? null,
            weight: r.weight != null ? Number(r.weight) : null,
            dispatchNumber: r.dispatchNumber ?? null,
            pickingListNumber: r.pickingListNumber ?? null,
            bags: r.bags ?? null,
            fclLcl: r.fclLcl ?? null,
            label: r.label ?? null,
            technicalSheet: r.technicalSheet ?? null,
            dispatchDate: fmt(r.dispatchDate),
            status: r.status ?? s?.status ?? null,
            updatedAt: fmt(r.updatedAt) ?? '',
          }
        })
      )
      setTotalCount(meta?.total ?? raw.length)
    } catch (err: unknown) {
      setRows([])
      setImportError(err instanceof Error ? err.message : 'Failed to load data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const fmt = (v: unknown): string | null => {
    if (v === undefined || v === null) return null
    if (typeof v === 'object' && 'toISOString' in (v as Date)) return (v as Date).toISOString()
    return String(v)
  }

  useEffect(() => {
    fetchStockRecon()
  }, [currentPage, debouncedSearch, debouncedLocation, resultsPerPage, stockView])

  useEffect(() => {
    const q = searchParams.get('search')
    if (q !== null) setSearchQuery(q)
  }, [searchParams])

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setImportError('Please select an Excel file (.xlsx or .xls)')
      return
    }
    try {
      setImporting(true)
      setImportError(null)
      const formData = new FormData()
      formData.append('file', file)
      const token = localStorage.getItem('token')
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
      const res = await fetch(`${baseUrl}/inventory/stock-import/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error?.message || data?.message || 'Import failed')
      if (data?.success && data?.data) {
        setImportError(null)
        fetchStockRecon()
      } else {
        throw new Error(data?.message || 'Import failed')
      }
    } catch (err: any) {
      setImportError(err?.message || 'Import failed. Make sure you are logged in.')
    } finally {
      setImporting(false)
      e.target.value = ''
    }
  }
  void handleImportExcel

  const totalResults = totalCount
  const totalPages = Math.max(1, Math.ceil(totalResults / resultsPerPage))
  const currentData = rows

  const thStyle = {
    fontFamily: 'Lexend Deca',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '20px',
    color: '#69697A',
    padding: '8px 6px',
    borderTop: '1px solid #E9E9EA',
    borderBottom: '1px solid #E9E9EA',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
  }
  const tdStyle = {
    fontFamily: 'Lexend Deca',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '20px',
    color: '#212126',
    padding: '8px 6px',
    borderBottom: '1px solid #E9E9EA',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
  }

  return (
    <div className="w-full" style={{ maxWidth: '100%' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
        className="hidden"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
      {/* Header Section */}
      <div style={{ marginBottom: '16px' }}>
        {/* First Row: Title on left, Breadcrumbs on right */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" style={{ marginBottom: '12px' }}>
          <h1
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '20px',
              lineHeight: '32px',
              letterSpacing: '0%',
              color: '#212126',
              margin: 0,
            }}
          >
            Storage & Inventory
          </h1>

          {/* Breadcrumbs on right */}
          <span
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0%',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Storage & Inventory</span>
          </span>
        </div>

        {/* Second Row: Add button on right */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setIsAddPeanutLotModalOpen(true)}
            className="flex items-center justify-center text-white font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
            style={{
              width: '190px',
              height: '38px',
              backgroundColor: '#212126',
              borderRadius: '6px',
              gap: '12px',
            }}
          >
            <FiPlus className="w-4 h-4" />
            <span>Add Product Batch</span>
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Search and Filter Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" style={{ padding: '16px', paddingBottom: '16px', gap: '12px' }}>
          {/* Search Bar */}
          <div className="relative">
            <FiSearch
              className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
              style={{ 
                pointerEvents: 'none',
                left: '12px'
              }}
            />
            <input
              type="text"
              placeholder="Search by batch, barcode, container..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              style={{
                fontFamily: 'Lexend Deca',
                fontSize: '14px',
                lineHeight: '20px',
                color: '#212126',
                width: '292px',
                height: '34px',
                paddingTop: '10px',
                paddingRight: '12px',
                paddingBottom: '10px',
                paddingLeft: '40px', // 12px icon position + 16px icon width + 12px gap
                borderRadius: '8px',
                borderWidth: '1px',
                border: '1px solid #E9E9EA',
                backgroundColor: '#FFFFFF'
              }}
            />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1" role="group" aria-label="Stock view">
              {([
                { id: 'current', label: 'Current' },
                { id: 'ytd', label: 'YTD' },
              ] as const).map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    if (stockView !== option.id) {
                      setStockView(option.id)
                      setCurrentPage(1)
                    }
                  }}
                  className="px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity text-xs sm:text-sm"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    lineHeight: '22px',
                    color: stockView === option.id ? '#FFFFFF' : '#212126',
                    backgroundColor: stockView === option.id ? '#212126' : 'transparent',
                    border: '1px solid #E9E9EA',
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Filter by location..."
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{
                fontFamily: 'Lexend Deca',
                fontSize: '14px',
                width: '160px',
                height: '32px',
                padding: '6px 10px',
                borderRadius: '6px',
              }}
            />
          </div>
        </div>
        {importError && (
          <div className="mb-3 px-4 py-2 rounded-lg bg-red-50 text-red-700 text-sm" style={{ fontFamily: 'Lexend Deca' }}>
            {importError}
          </div>
        )}
        <div
          className="overflow-x-auto w-full"
          style={{ paddingLeft: '12px', paddingRight: '12px', paddingBottom: '0' }}
        >
          <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '4400px', minWidth: '100%' }}>
            <thead>
              <tr style={{ backgroundColor: '#FFFFFF' }}>
                {[
                  { label: 'Buy Contract Number', width: '140px' },
                  { label: 'Sale Contract Number', width: '145px' },
                  { label: 'Vessel/Voyage', width: '150px' },
                  { label: 'Booking', width: '120px' },
                  { label: 'Bill of Lading', width: '130px' },
                  { label: 'ETA', width: '125px' },
                  { label: 'ATA', width: '125px' },
                  { label: 'Container Number', width: '115px' },
                  { label: 'Batch Number', width: '100px' },
                  { label: 'Container Discharge Date', width: '145px' },
                  { label: 'Container Gate In', width: '120px' },
                  { label: 'Container Gate Out', width: '125px' },
                  { label: 'Unpack Date Time', width: '115px' },
                  { label: 'Days In Warehouse', width: '115px' },
                  { label: 'Location', width: '140px' },
                  { label: 'Marks And Numbers', width: '180px' },
                  { label: 'Product', width: '180px' },
                  { label: 'Barcode', width: '120px' },
                  { label: 'Pieces', width: '55px' },
                  { label: 'Item Number', width: '80px' },
                  { label: 'Weight', width: '60px' },
                  { label: 'Dispatch Number', width: '110px' },
                  { label: 'Picking List Number', width: '120px' },
                  { label: 'Bags', width: '120px' },
                  { label: 'Label', width: '160px' },
                  { label: 'Technical Sheet', width: '160px' },
                  { label: 'Dispatch Date', width: '110px' },
                  { label: 'Status', width: '100px' },
                  { label: 'Action', width: '55px' },
                ].map((col, i) => (
                  <th key={col.label} className="text-left" style={{ ...thStyle, width: col.width, borderLeft: i === 0 ? '1px solid #E9E9EA' : undefined, borderRight: i === 28 ? '1px solid #E9E9EA' : undefined }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={31} className="py-8 text-center text-gray-500" style={{ borderLeft: '1px solid #E9E9EA', borderBottom: '1px solid #E9E9EA' }}>
                    Loading...
                  </td>
                </tr>
              ) : (
              currentData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td style={{ ...tdStyle, borderLeft: '1px solid #E9E9EA' }} title={item.buyContractNumber ?? ''}>{item.buyContractNumber || '—'}</td>
                    <td style={tdStyle} title={item.saleContractNumber ?? ''}>{item.saleContractNumber || '—'}</td>
                    <td style={tdStyle} title={item.vesselVoyage ?? ''}>{item.vesselVoyage || '—'}</td>
                    <td style={tdStyle} title={item.booking ?? ''}>{item.booking || '—'}</td>
                    <td style={tdStyle} title={item.billOfLading ?? ''}>{item.billOfLading || '—'}</td>
                    <td style={tdStyle}>{item.eta ? formatDateTimeToDDMMYYYYHHMM(item.eta) : '—'}</td>
                    <td style={tdStyle}>{item.ata ? formatDateTimeToDDMMYYYYHHMM(item.ata) : '—'}</td>
                    <td style={tdStyle} title={item.containerNumber ?? ''}>{item.containerNumber || '—'}</td>
                    <td style={tdStyle} title={item.batchNumber ?? ''}>{item.batchNumber || '—'}</td>
                    <td style={tdStyle}>{item.containerDischargeDate ? formatDateTimeToDDMMYYYYHHMM(item.containerDischargeDate) : '—'}</td>
                    <td style={tdStyle}>{item.containerGateIn ? formatDateTimeToDDMMYYYYHHMM(item.containerGateIn) : '—'}</td>
                    <td style={tdStyle}>{item.containerGateOut ? formatDateTimeToDDMMYYYYHHMM(item.containerGateOut) : '—'}</td>
                    <td style={tdStyle}>{item.unpackDateTime ? formatDateTimeToDDMMYYYYHHMM(item.unpackDateTime) : '—'}</td>
                    <td style={tdStyle}>{item.daysInWarehouse ?? '—'}</td>
                    <td style={tdStyle} title={item.location ?? ''}>{item.location || '—'}</td>
                    <td style={tdStyle} title={item.marksAndNumbers ?? ''}>{item.marksAndNumbers || '—'}</td>
                    <td style={tdStyle} title={item.product ?? ''}>{item.product || '—'}</td>
                    <td style={tdStyle} title={item.barcode ?? ''}>{item.barcode || '—'}</td>
                    <td style={tdStyle}>{item.pieces ?? '—'}</td>
                    <td style={tdStyle}>{item.itemNumber || '—'}</td>
                    <td style={tdStyle}>{item.weight ?? '—'}</td>
                    <td style={tdStyle}>{item.dispatchNumber || '—'}</td>
                    <td style={tdStyle}>{item.pickingListNumber || '—'}</td>
                    {/* Bags: "X / Y FCL" with progress bar */}
                    <td style={tdStyle}>
                      {item.bags != null || item.pieces != null ? (() => {
                        const current = item.bags ?? 0
                        const total = item.pieces ?? current
                        const unit = item.fclLcl || 'FCL'
                        const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0
                        const isDispatched = item.status?.toLowerCase() === 'dispatched'
                        const barColor = isDispatched ? '#EF4444' : '#10B981'
                        return (
                          <div>
                            <span style={{ fontFamily: 'Lexend Deca', fontSize: '12px', fontWeight: 500, color: '#212126' }}>
                              {current} / {total} {unit}
                            </span>
                            <div style={{ marginTop: '4px', height: '4px', width: '80px', backgroundColor: '#E9E9EA', borderRadius: '2px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct}%`, backgroundColor: barColor, borderRadius: '2px', transition: 'width 0.3s' }} />
                            </div>
                          </div>
                        )
                      })() : '—'}
                    </td>
                    {/* Label: Upload / View */}
                    <td style={tdStyle}>
                      {item.label ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => viewStockReconFile(item.id, 'label')}
                            style={{
                              fontFamily: 'Lexend Deca', fontSize: '11px', fontWeight: 500,
                              color: '#13AB5B', backgroundColor: '#E8F7EF',
                              border: '1px solid #13AB5B', borderRadius: '4px',
                              padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                            className="hover:opacity-80 transition-opacity"
                          >
                            View
                          </button>
                          <button
                            onClick={() => triggerFileUpload(item.id, 'label')}
                            disabled={uploadingRowField === `${item.id}-label`}
                            style={{
                              fontFamily: 'Lexend Deca', fontSize: '11px', fontWeight: 500,
                              color: '#2C71F6', backgroundColor: '#EAF1FF',
                              border: '1px solid #D0E1FF', borderRadius: '4px',
                              padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                            className="hover:opacity-80 transition-opacity"
                          >
                            {uploadingRowField === `${item.id}-label` ? '...' : 'Replace'}
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                          <button
                            onClick={() => triggerFileUpload(item.id, 'label')}
                            disabled={uploadingRowField === `${item.id}-label`}
                            style={{
                              fontFamily: 'Lexend Deca', fontSize: '11px', fontWeight: 500,
                              color: '#2C71F6', backgroundColor: '#EAF1FF',
                              border: '1px solid #D0E1FF', borderRadius: '4px',
                              padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                            className="hover:opacity-80 transition-opacity"
                          >
                            {uploadingRowField === `${item.id}-label` ? 'Uploading...' : 'Upload Label'}
                          </button>
                          <span style={{ fontFamily: 'Lexend Deca', fontSize: '10px', color: '#9CA3AF' }}>
                            LOT: {item.batchNumber || 'NA'}
                          </span>
                        </div>
                      )}
                    </td>
                    {/* Technical Sheet: Upload / View */}
                    <td style={tdStyle}>
                      {item.technicalSheet ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => viewStockReconFile(item.id, 'technicalSheet')}
                            style={{
                              fontFamily: 'Lexend Deca', fontSize: '11px', fontWeight: 500,
                              color: '#13AB5B', backgroundColor: '#E8F7EF',
                              border: '1px solid #13AB5B', borderRadius: '4px',
                              padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                            className="hover:opacity-80 transition-opacity"
                          >
                            View
                          </button>
                          <button
                            onClick={() => triggerFileUpload(item.id, 'technicalSheet')}
                            disabled={uploadingRowField === `${item.id}-technicalSheet`}
                            style={{
                              fontFamily: 'Lexend Deca', fontSize: '11px', fontWeight: 500,
                              color: '#2C71F6', backgroundColor: '#EAF1FF',
                              border: '1px solid #D0E1FF', borderRadius: '4px',
                              padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                            className="hover:opacity-80 transition-opacity"
                          >
                            {uploadingRowField === `${item.id}-technicalSheet` ? '...' : 'Replace'}
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                          <button
                            onClick={() => triggerFileUpload(item.id, 'technicalSheet')}
                            disabled={uploadingRowField === `${item.id}-technicalSheet`}
                            style={{
                              fontFamily: 'Lexend Deca', fontSize: '11px', fontWeight: 500,
                              color: '#2C71F6', backgroundColor: '#EAF1FF',
                              border: '1px solid #D0E1FF', borderRadius: '4px',
                              padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                            className="hover:opacity-80 transition-opacity"
                          >
                            {uploadingRowField === `${item.id}-technicalSheet` ? 'Uploading...' : 'Upload Sheet'}
                          </button>
                          <span style={{ fontFamily: 'Lexend Deca', fontSize: '10px', color: '#9CA3AF' }}>
                            LOT: {item.batchNumber || 'NA'}
                          </span>
                        </div>
                      )}
                    </td>
                    {/* Dispatch Date: DD MMM YYYY format */}
                    <td style={tdStyle}>{item.dispatchDate ? formatDateToDDMMMYYYY(item.dispatchDate) : '—'}</td>
                    {/* Status – inline editable */}
                    <td style={{ ...tdStyle, padding: '4px 8px' }}>
                      <input
                        type="text"
                        value={statusEdits[item.id] ?? (item.status || '')}
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                        onBlur={() => saveStatus(item.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                        placeholder="Enter status"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontSize: '12px',
                          lineHeight: '20px',
                          color: '#212126',
                          width: '100%',
                          minWidth: '80px',
                          height: '30px',
                          padding: '4px 8px',
                          border: '1px solid #E9E9EA',
                          borderRadius: '6px',
                          backgroundColor: '#FFFFFF',
                          outline: 'none',
                        }}
                      />
                    </td>
                    <td style={{ ...tdStyle, borderRight: '1px solid #E9E9EA', position: 'relative', overflow: 'visible' }}>
                      <div style={{ position: 'relative', display: 'inline-block' }} data-dropdown>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleDropdown(item.id)
                          }}
                          className="hover:opacity-80 rounded transition-colors"
                          title="More options"
                          style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#EAF1FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                          }}
                        >
                          <FiMoreVertical 
                            style={{ 
                              width: '14px', 
                              height: '14px', 
                              color: '#2C71F6' 
                            }} 
                          />
                        </button>
                        {openDropdownId === item.id && (
                          <div
                            data-dropdown
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: '100%',
                              marginTop: '4px',
                              backgroundColor: 'white',
                              border: '1px solid #E9E9EA',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                              zIndex: 1000,
                              minWidth: '120px',
                              padding: '4px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenDropdownId(null)
                                // Handle Edit action
                              }}
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '14px',
                                color: '#212126'
                              }}
                              className="hover:bg-gray-50 rounded transition-colors"
                            >
                              <div style={{ width: '24px', height: '24px', backgroundColor: '#FEF9EB', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FiEdit style={{ width: '14px', height: '14px', color: '#F59E0B' }} />
                              </div>
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenDropdownId(null)
                                setSelectedRow(item)
                                setIsPeanutLotDetailsModalOpen(true)
                              }}
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '14px',
                                color: '#212126'
                              }}
                              className="hover:bg-gray-50 rounded transition-colors"
                            >
                              <div style={{ width: '24px', height: '24px', backgroundColor: '#EAF1FF', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FiEye style={{ width: '14px', height: '14px', color: '#2C71F6' }} />
                              </div>
                              <span>View</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenDropdownId(null)
                                setSelectedRow(item)
                                setIsLotTraceabilityModalOpen(true)
                              }}
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '14px',
                                color: '#212126'
                              }}
                              className="hover:bg-gray-50 rounded transition-colors"
                            >
                              <div style={{ width: '24px', height: '24px', backgroundColor: '#F2F4F2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <img src="/images/map.svg" alt="Traceability" style={{ width: '14px', height: '14px' }} />
                              </div>
                              <span>Traceability</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenDropdownId(null)
                                // Handle Download action
                              }}
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '14px',
                                color: '#212126'
                              }}
                              className="hover:bg-gray-50 rounded transition-colors"
                            >
                              <div style={{ width: '24px', height: '24px', backgroundColor: '#E8F7EF', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FiDownload style={{ width: '14px', height: '14px', color: '#10B981' }} />
                              </div>
                              <span>Download</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenDropdownId(null)
                                // Handle Delete action
                              }}
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '14px',
                                color: '#212126'
                              }}
                              className="hover:bg-gray-50 rounded transition-colors"
                            >
                              <div style={{ width: '24px', height: '24px', backgroundColor: '#FEE2E2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FiTrash2 style={{ width: '14px', height: '14px', color: '#EF4444' }} />
                              </div>
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination bar - full width below table so it stays visible and uses full space */}
        <div
          className="flex flex-col sm:flex-row items-center justify-between gap-3 w-full py-3 px-4 sm:px-6"
          style={{
            borderTop: '1px solid #E9E9EA',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            backgroundColor: '#FFFFFF'
          }}
        >
          <span
            className="text-xs sm:text-sm"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              lineHeight: '22px',
              color: '#69697A'
            }}
          >
            Showing {currentData.length} of {totalResults} Results
          </span>
          <div className="flex items-center gap-2">
            <PageSizeSelect
              value={resultsPerPage}
              onChange={(size) => {
                setResultsPerPage(size)
                setCurrentPage(1)
              }}
            />
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                lineHeight: '22px',
                color: '#212126',
                border: '1px solid #E9E9EA',
                backgroundColor: 'transparent'
              }}
            >
              <FiChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Previous</span>
            </button>
            <span
              className="text-xs sm:text-sm px-2"
              style={{ fontFamily: 'Lexend Deca', fontWeight: 400, lineHeight: '22px', color: '#69697A' }}
            >
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                lineHeight: '22px',
                color: '#212126',
                border: '1px solid #E9E9EA',
                backgroundColor: 'transparent'
              }}
            >
              <span className="hidden sm:inline">Next</span>
              <FiChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Add Peanut Lot Modal */}
      <AddPeanutLotModal
        isOpen={isAddPeanutLotModalOpen}
        onClose={() => setIsAddPeanutLotModalOpen(false)}
        onLotAdded={(lot) => {
          // Handle lot addition
          console.log('Lot added:', lot)
        }}
      />

      {/* Peanut Lot Details Modal */}
      <PeanutLotDetailsModal
        isOpen={isPeanutLotDetailsModalOpen}
        onClose={() => {
          setIsPeanutLotDetailsModalOpen(false)
          setSelectedRow(null)
        }}
        data={selectedRow ? {
          product: selectedRow.product ?? '',
          productDescription: '',
          lot: selectedRow.batchNumber ?? '',
          bags: String(selectedRow.bags ?? selectedRow.pieces ?? 0),
          bagsCapacity: String(selectedRow.bags ?? selectedRow.pieces ?? 0),
          bagsUnit: '',
          bagsProgress: 100,
          bagsProgressColor: 'green' as const,
          location: selectedRow.location ?? '',
          label: selectedRow.label ?? null,
          coa: selectedRow.technicalSheet ?? null,
          buyContractNumber: selectedRow.buyContractNumber ?? '',
          saleContractNumber: selectedRow.saleContractNumber ?? '',
          lastUpdated: selectedRow.updatedAt,
          status: selectedRow.status ?? ''
        } : null}
      />

      <LotTraceabilityModal
        isOpen={isLotTraceabilityModalOpen}
        onClose={() => {
          setIsLotTraceabilityModalOpen(false)
          setSelectedRow(null)
        }}
        rowId={selectedRow?.id}
        data={selectedRow ? {
          product: selectedRow.product ?? '',
          productDescription: '',
          lot: selectedRow.batchNumber ?? '',
          bags: selectedRow.bags ?? selectedRow.pieces ?? 0,
          bagsCapacity: selectedRow.bags ?? selectedRow.pieces ?? 0,
          bagsUnit: '',
          location: selectedRow.location ?? '',
          status: selectedRow.status ?? '',
          lastUpdated: selectedRow.updatedAt
        } : undefined}
      />
    </div>
  )
}

export default StorageInventory
